/* eslint-disable prefer-arrow-functions/prefer-arrow-functions */

"use client";

import { Button } from "@/components/button";
import { Input } from "@/components/input";
import Link from "next/link";
import React from "react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SignInCredentials } from "@/types/user";
import { authService } from "@/services/auth";
import { error, success } from "@/components/alert";
import NetworkError from "@/utils/network-error";
import useUserStore from "@/global-store/user";
import { useRouter, useSearchParams } from "next/navigation";
import useCartStore from "@/global-store/cart";
import { useSyncServer } from "@/hook/use-sync-server";
import { setLoginCookie } from "@/utils/auth-cookie";
import { clearAppCartStorage } from "@/utils/clear-app-cart";

const schema = yup.object({
  email: yup
    .string()
    .required("Email is required")
    .email("Invalid email address"),
  password: yup
    .string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters long"),
});

type FormType = yup.InferType<typeof schema>;

function SignInForm() {
  const queryClient = useQueryClient();
  const { handleSync } = useSyncServer();
  const clearCart = useCartStore((state) => state.clear);
  const deleteMemberData = useCartStore((state) => state.deleteMemberData);
  const { mutate: signIn, isLoading: isSigningIn } = useMutation({
    mutationFn: (body: SignInCredentials) => authService.signIn(body),
    onError: (err: NetworkError) => {
      error(err.message);
    },
  });
  const localSignIn = useUserStore((state) => state.signIn);
  const setMe = useUserStore((state) => state.setMe);
  const router = useRouter();
  const searchParams = useSearchParams();

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<FormType>({
    resolver: yupResolver(schema),
  });

  const handleLogin = (data: FormType) => {
    const body: SignInCredentials = {
      password: data.password,
      email: data.email,
    };
    signIn(body, {
      onSuccess: async (res) => {
        success("successfully logged in");
        localSignIn({
          ...res.data,
          firstname: res.data.firstName,
          lastname: res.data.lastName,
          img: res.data.image,
          phone: res.data.contactNo,
        });
        setLoginCookie();

        clearCart();
        deleteMemberData();
        clearAppCartStorage();

        await authService.refresh();

        try {
          const meRes = await authService.getMe();
          if (meRes?.data) setMe(meRes.data);
        } catch {
          // ignore; me can be fetched later (e.g. Navbar)
        }

        handleSync();
        if (searchParams.has("redirect")) {
          router.replace(searchParams.get("redirect") as string);
        } else {
          router.replace("/");
        }
      },
    });
  };

  return (
    <div className="w-full">
      <h1 className="text-2xl font-bold text-center mb-6 text-black">
        Sign In
      </h1>

      <form onSubmit={handleSubmit(handleLogin)} className="space-y-4">
        <Input
          {...register("email")}
          error={errors.email?.message}
          fullWidth
          label="Email"
          required
          placeholder="Enter your email"
          type="email"
        />

        <Input
          {...register("password")}
          error={errors.password?.message}
          fullWidth
          label="Password"
          required
          type="password"
        />

        <Button
          type="submit"
          loading={isSigningIn}
          disabled={Boolean(queryClient.isMutating())}
          fullWidth
          className="!mt-6"
        >
          Continue
        </Button>
      </form>

      <div className="flex items-center justify-between mt-6 text-sm">
        <Link
          href="/forgot-password"
          className="text-gray-600 hover:text-primary transition-colors"
        >
          Forgot password?
        </Link>
      </div>
    </div>
  );
}

export default function SignInPage() {
  return (
    <React.Suspense
      fallback={
        <div className="w-full min-h-[200px] flex items-center justify-center">
          Loading…
        </div>
      }
    >
      <SignInForm />
    </React.Suspense>
  );
}
